import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const TronContext = createContext({
  connected: false,
  address: null,
  tronWeb: null,
  connect: async () => {},
  disconnect: () => {},
  connecting: false,
});

function getTronProvider() {
  if (typeof window === "undefined") return null;
  if (window.tron?.tronWeb) return window.tron;
  if (window.tronWeb) return { tronWeb: window.tronWeb, legacy: true };
  if (window.tronLink?.tronWeb) return window.tronLink;
  return null;
}

export function TronProvider({ children }) {
  const [address, setAddress] = useState(null);
  const [tronWeb, setTronWeb] = useState(null);
  const [connecting, setConnecting] = useState(false);

  // Auto-detect if already unlocked
  useEffect(() => {
    const p = getTronProvider();
    const stored = typeof window !== "undefined" ? localStorage.getItem("dex-tron-connected") : null;
    if (p && stored === "1") {
      const tw = p.tronWeb || window.tronWeb;
      const addr = tw?.defaultAddress?.base58;
      if (addr) { setTronWeb(tw); setAddress(addr); }
    }
  }, []);

  const connect = useCallback(async () => {
    setConnecting(true);
    try {
      const provider = getTronProvider();
      if (!provider) {
        window.open("https://www.tronlink.org/", "_blank");
        throw new Error("Install TronLink to continue");
      }
      // Modern TronLink / OKX
      if (provider.request) {
        try { await provider.request({ method: "eth_requestAccounts" }); }
        catch { await provider.request({ method: "tron_requestAccounts" }); }
      } else if (window.tronLink?.request) {
        await window.tronLink.request({ method: "tron_requestAccounts" });
      }
      const tw = provider.tronWeb || window.tronWeb || window.tronLink?.tronWeb;
      const addr = tw?.defaultAddress?.base58;
      if (!tw || !addr) throw new Error("Approve the wallet connection first");
      setTronWeb(tw);
      setAddress(addr);
      localStorage.setItem("dex-tron-connected", "1");
    } finally {
      setConnecting(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    setAddress(null);
    setTronWeb(null);
    localStorage.removeItem("dex-tron-connected");
  }, []);

  return (
    <TronContext.Provider value={{ address, tronWeb, connected: !!address, connect, disconnect, connecting }}>
      {children}
    </TronContext.Provider>
  );
}

export function useTronWallet() { return useContext(TronContext); }

export async function signAndBroadcastTron({ tronWeb, transactionRequest }) {
  let rawTx = transactionRequest?.data;
  if (!rawTx) throw new Error("Quote missing Tron transaction data");
  if (typeof rawTx === "string") {
    try { rawTx = JSON.parse(rawTx); }
    catch { throw new Error("Unexpected Tron transaction encoding"); }
  }
  if (typeof rawTx !== "object" || (!rawTx.txID && !rawTx.raw_data)) {
    throw new Error("Invalid Tron raw transaction");
  }
  const signed = await tronWeb.trx.sign(rawTx);
  const result = await tronWeb.trx.sendRawTransaction(signed);
  if (!result?.result || !result?.txid) {
    throw new Error(`Tron broadcast failed: ${result?.code || "unknown error"}`);
  }
  return result.txid;
}
